/**
 * Single source of truth for the device-API origin.
 *
 * PRODUCTION: the Kegtron Pro compat layer (pro-compat.api.kegtron.com),
 * cut over from mdash.net on 2026-09-01. The compat Worker routes each request
 * per device — devices still registered in mDash are proxied to mdash.net
 * verbatim, migrated devices are served from supaDash.
 * Validation before cutover: 130-org read-only replay (0 diffs), staging
 * click-through, live notify frames on both backends.
 *
 * REST always goes to API_ORIGIN. The notify WebSocket does NOT: relaying an
 * mDash-backed socket through the compat layer holds a Durable Object open for
 * the life of the connection (it cannot hibernate while a client is attached),
 * which is the account's only metered cost. Since the compat layer began
 * reporting `X-Kegtron-Backend` on `GET /api/v2/m/device`, the app routes those
 * sockets straight to MDASH_ORIGIN instead — see services/deviceBackend.js and
 * services/websocketService.js.
 *
 * NOTE: this is hardcoded rather than env-driven on purpose. preact-cli only
 * DefinePlugin-substitutes `PREACT_APP_*` keys that are actually present in
 * `process.env` at build time; an unset key survives into the bundle as a
 * literal `process.env.PREACT_APP_…`, which throws `process is not defined`
 * in the browser. There is no existing `process.env` usage in src/ to
 * piggyback on, so a hardcoded constant is the safe option here.
 *
 * Imported by the service worker as well as app code, so keep this module
 * free of side effects and free of DOM/app dependencies.
 */

/** Origin the app talks to for all device/customer API calls. */
export const API_ORIGIN = 'https://pro-compat.api.kegtron.com';

/**
 * Canonical mDash origin. Two uses, both deliberate:
 *
 *  1. The `url` field of the account-email payload posted to `/invite`
 *     (registration invite + password reset) — see below.
 *  2. The notify WebSocket host for devices the compat layer reports as
 *     mDash-backed (services/websocketService.js). mDash accepts a socket from
 *     any Origin and WebSockets are not CORS-gated, so this is exactly what the
 *     app did before the 2026-09-01 cutover.
 *
 * mDash validates that field against its own canonical origin and rejects
 * anything else with `500 {"error":{"message":"bad URL"}}` — including
 * `http://mdash.net` and the compat origin above. Sending API_ORIGIN there
 * (which is what happened after the 2026-09-01 cutover, since both values came
 * from Configuration.mdashURL) broke "Forgot Password?" and "Register" with a
 * generic "Something went wrong". Keep this decoupled from API_ORIGIN: the
 * device-API origin may move again, this one may not.
 */
export const MDASH_ORIGIN = 'https://mdash.net';

/**
 * Hostnames that serve the mDash-compatible device API. The legacy mdash.net
 * host is retained so a client that still has an in-flight legacy URL (or a
 * cached SW route) keeps behaving identically.
 */
export const API_HOSTS = ['pro-compat.api.kegtron.com', 'mdash.net'];

/**
 * True when a URL (or bare hostname) belongs to the device API.
 * @param {string} value - Full URL or hostname
 * @returns {boolean}
 */
export function isApiUrl(value) {
  if (!value) return false;
  return API_HOSTS.some((host) => value.includes(host));
}
